import { useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { lenisRef } from "@/lib/smooth";

export const Lightbox = ({ images, index, onChange, onClose }) => {
  const open = index !== null && index !== undefined;
  const total = images.length;
  const go = useCallback((d) => onChange((index + d + total) % total), [index, total, onChange]);

  useEffect(() => {
    if (!open) return undefined;
    lenisRef.current?.stop();
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      lenisRef.current?.start();
    };
  }, [open, go, onClose]);

  const im = open ? images[index] : null;

  if (!open) return null;
  return createPortal(
    <>
      {(
        <motion.div
          data-testid="menu-lightbox"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={onClose}
          className="fixed inset-0 z-[85] flex flex-col items-center justify-center bg-espresso/95 px-4 py-16 backdrop-blur-md"
        >
          <button data-testid="menu-lightbox-close" onClick={onClose} aria-label="Cerrar" className="absolute right-4 top-4 rounded-full border border-gold/40 p-3 text-cream transition-colors hover:bg-gold hover:text-espresso">
            <X size={18} />
          </button>
          <p data-testid="menu-lightbox-counter" className="absolute left-5 top-6 font-sans text-xs uppercase tracking-[0.3em] text-gold">
            {index + 1} / {total}
          </p>

          <div className="relative flex w-full max-w-5xl flex-1 items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <motion.img
              key={im.src}
              src={im.src}
              alt={im.cap}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35 }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.4}
              onDragEnd={(_, info) => {
                if (info.offset.x < -60) go(1);
                else if (info.offset.x > 60) go(-1);
              }}
              className="max-h-[72vh] max-w-full cursor-grab rounded-2xl object-contain active:cursor-grabbing"
            />
          </div>

          <p data-testid="menu-lightbox-caption" className="mt-5 font-display text-2xl italic text-cream" onClick={(e) => e.stopPropagation()}>
            {im.cap}
          </p>

          {total > 1 && (
            <>
              <button
                data-testid="menu-lightbox-prev"
                onClick={(e) => {
                  e.stopPropagation();
                  go(-1);
                }}
                aria-label="Anterior"
                className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full border border-gold/40 bg-espresso/60 p-3 text-cream transition-colors hover:bg-gold hover:text-espresso md:left-8 md:p-4"
              >
                <ChevronLeft size={22} />
              </button>
              <button
                data-testid="menu-lightbox-next"
                onClick={(e) => {
                  e.stopPropagation();
                  go(1);
                }}
                aria-label="Siguiente"
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full border border-gold/40 bg-espresso/60 p-3 text-cream transition-colors hover:bg-gold hover:text-espresso md:right-8 md:p-4"
              >
                <ChevronRight size={22} />
              </button>
            </>
          )}
        </motion.div>
      )}
    </>,
    document.body
  );
};
